import { Link } from 'react-router';

import type { CycleStandingResponse, ReviewCycleSummaryResponse } from '../../api';
import { AsyncSection, DataTable, PageHeader, Panel, type Column } from '../../components';
import { formatCycleMonth, formatDate, formatInteger } from '../../format';
import { PATHS, staffSubmissionContractPath } from '../../routes/paths';
import { useHealth, useMailMessages, useReviewQueue, useSubmissionCalendar } from '../queries';
import { OVERVIEW_COPY, stageTone } from './overview';
import { REVIEW_COPY } from './reviewQueue';
import { STAGE_LABELS, currentCycleMonth } from './submissionCycle';

/**
 * The screen an analyst opens first, and the one they should be able to close
 * again without opening anything else.
 *
 * It answers one question — **is there anything today that will not get done by
 * itself?** — and it answers it from the three places work stalls, in the order
 * they stall: a month whose file has not gone out, a movement nobody has read,
 * and a message that arrived by e-mail and stopped. Everything else the
 * back-office knows is deliberately absent. A dashboard that shows everything is
 * a dashboard that gets skimmed, and this one exists to be *read*.
 *
 * Every number here is one the operational screens already fetch, through the
 * same hooks and therefore the same query keys. That is not only an economy: it
 * means the overview and the screen it links to cannot disagree. An analyst who
 * reads "3 a enviar" here and opens the submissions page is looking at the
 * cached answer to the identical request, not at a second count assembled
 * differently. The page adds no endpoint of its own for the same reason.
 *
 * The month is the current one, resolved exactly as the review and submissions
 * screens resolve theirs, so all three agree on which cycle "this month" is
 * without anyone choosing. There is no month picker: picking a month is what the
 * screens underneath are for, and this one is about today.
 *
 * The one thing on the page allowed to shout is the retroactive count. A
 * movement dated inside a month the insurer already has cannot be filed by this
 * product at all — it needs a person, outside the system, and it is the only
 * item here whose cost goes up while it waits.
 */
export function StaffOverviewPage() {
  const cycleMonth = currentCycleMonth();

  const calendar = useSubmissionCalendar(cycleMonth);
  const queue = useReviewQueue(cycleMonth);
  // The same filter the mailbox screen opens on, so the two share a cache entry
  // rather than asking the same question twice with different keys.
  const mailbox = useMailMessages(true);
  const health = useHealth();

  return (
    <div className="page">
      <PageHeader
        title={OVERVIEW_COPY.title}
        description={OVERVIEW_COPY.description(formatCycleMonth(cycleMonth))}
      />

      <Panel title={OVERVIEW_COPY.standingTitle} description={OVERVIEW_COPY.standingDescription}>
        <AsyncSection
          state={calendar}
          loadingLabel={OVERVIEW_COPY.standingLoading}
          errorTitle={OVERVIEW_COPY.standingError}
          isEmpty={(data) => data.items.length === 0}
          emptyTitle={OVERVIEW_COPY.noContractsTitle}
          emptyDescription={OVERVIEW_COPY.noContracts}
        >
          {(data) => (
            <>
              <dl className="summary">
                <div className="summary__item">
                  <dt>{OVERVIEW_COPY.contracts}</dt>
                  <dd>{formatInteger(data.count)}</dd>
                </div>
                <div className="summary__item">
                  <dt>{OVERVIEW_COPY.outstanding}</dt>
                  <dd>{formatInteger(data.outstanding_count)}</dd>
                </div>
                <div className="summary__item">
                  <dt>{OVERVIEW_COPY.overdue}</dt>
                  <dd>{formatInteger(data.overdue_count)}</dd>
                </div>
              </dl>
              <DataTable
                id="overview-cycle-close"
                caption="Contratos e o fechamento deste ciclo"
                columns={STANDING_COLUMNS}
                rows={data.items}
                rowKey={(row) => row.contract_id}
                emptyTitle={OVERVIEW_COPY.noContractsTitle}
              />
            </>
          )}
        </AsyncSection>
      </Panel>

      <Panel title={OVERVIEW_COPY.workloadTitle} description={OVERVIEW_COPY.workloadDescription}>
        <AsyncSection
          state={queue}
          loadingLabel={OVERVIEW_COPY.workloadLoading}
          errorTitle={OVERVIEW_COPY.workloadError}
          isEmpty={(data) => data.items.length === 0}
          emptyTitle={OVERVIEW_COPY.noQueueTitle}
          emptyDescription={OVERVIEW_COPY.noQueue}
          emptyAction={<Link to={PATHS.staffReview}>{OVERVIEW_COPY.openReviewQueue}</Link>}
        >
          {(data) => (
            <>
              <DataTable
                caption="Contratos e o que espera análise em cada um"
                columnVisibility={false}
                columns={WORKLOAD_COLUMNS}
                rows={data.items}
                rowKey={(row) => row.contract_id}
                emptyTitle={OVERVIEW_COPY.noQueueTitle}
              />
              <RetroactiveFootnote items={data.items} />
              <p className="panel__footnote">
                <Link to={PATHS.staffReview}>{OVERVIEW_COPY.openReviewQueue}</Link>
              </p>
            </>
          )}
        </AsyncSection>
      </Panel>

      <Panel title={OVERVIEW_COPY.mailboxTitle} description={OVERVIEW_COPY.mailboxDescription}>
        <AsyncSection
          state={mailbox}
          loadingLabel={OVERVIEW_COPY.mailboxLoading}
          errorTitle={OVERVIEW_COPY.mailboxError}
        >
          {(data) => (
            <>
              {/* A sentence either way, never a table. The listing is the
                  mailbox screen's job; what belongs here is the one number,
                  and "nada aguardando" is a result worth reading rather than
                  an empty panel to interpret. */}
              {data.outstanding_count === 0 ? (
                <p>{OVERVIEW_COPY.mailboxClear}</p>
              ) : (
                <p className="panel__footnote panel__footnote--warning">
                  {OVERVIEW_COPY.mailboxWaiting(formatInteger(data.outstanding_count))}
                </p>
              )}
              <p className="panel__footnote">
                <Link to={PATHS.staffMailbox}>{OVERVIEW_COPY.openMailbox}</Link>
              </p>
            </>
          )}
        </AsyncSection>
      </Panel>

      <Panel title="Estado do sistema" description="GET /api/v1/health.">
        <AsyncSection
          state={health}
          loadingLabel="Consultando o sistema…"
          errorTitle="Não foi possível consultar o sistema"
        >
          {(data) => (
            <dl className="definitions">
              <div>
                <dt>Situação</dt>
                <dd>{data.status === 'ok' ? 'Operando normalmente' : 'Degradado'}</dd>
              </div>
              <div>
                <dt>Ambiente</dt>
                <dd>{data.environment}</dd>
              </div>
              <div>
                <dt>Versão</dt>
                <dd>{data.version}</dd>
              </div>
              <div>
                <dt>Banco de dados</dt>
                <dd>{data.database.status === 'ok' ? 'Respondendo' : 'Indisponível'}</dd>
              </div>
            </dl>
          )}
        </AsyncSection>
      </Panel>
    </div>
  );
}

/**
 * One contract's month, and the way into it.
 *
 * The company name is the link rather than a button in an actions column,
 * because the row *is* the link: there is nothing else to do with a line on this
 * page but go and work on it. It goes straight to the contract's own page in
 * the submissions rhythm, so the overview, the bookmark and a link pasted to a
 * colleague all end at the same address.
 */
const STANDING_COLUMNS: readonly Column<CycleStandingResponse>[] = [
  {
    key: 'company',
    header: 'Cliente',
    render: (row) => (
      <Link
        to={staffSubmissionContractPath(row.contract_id, row.cycle_month)}
        title={OVERVIEW_COPY.openInSubmissions}
      >
        {row.company_name}
      </Link>
    ),
  },
  {
    key: 'insurer',
    header: 'Operadora',
    render: (row) => (
      <>
        {row.insurer_name}
        <br />
        <span className="table__muted">
          {row.subestipulante} / {row.modulo}
        </span>
      </>
    ),
  },
  { key: 'cutoff', header: 'Corte', render: (row) => formatDate(row.cutoff_date) },
  {
    key: 'stage',
    header: 'Situação',
    render: (row) => (
      <span className={`badge ${stageTone(row.stage)}`}>{STAGE_LABELS[row.stage]}</span>
    ),
  },
  {
    key: 'overdue',
    header: 'Prazo',
    render: (row) =>
      row.is_overdue ? (
        <span className="badge badge--warning">{OVERVIEW_COPY.overdue}</span>
      ) : (
        <span className="table__muted">—</span>
      ),
  },
];

/**
 * One contract's reading workload.
 *
 * Two numbers, and they are not the same kind of number. "Em análise" is work
 * that will be done this month by somebody sitting down to it; "fora do
 * período" is work that *cannot* be done here at all, and it is badged rather
 * than printed so it is visible at a glance across a table of contracts. Zero
 * stays quiet, because a column of amber zeroes teaches an analyst to stop
 * seeing amber.
 */
const WORKLOAD_COLUMNS: readonly Column<ReviewCycleSummaryResponse>[] = [
  { key: 'company', header: 'Cliente', render: (row) => row.company_name },
  {
    key: 'items',
    header: OVERVIEW_COPY.inReview,
    align: 'end',
    render: (row) => formatInteger(row.item_count),
  },
  {
    key: 'late',
    header: OVERVIEW_COPY.retroactive,
    headerHint: REVIEW_COPY.lateDescription,
    align: 'end',
    render: (row) =>
      row.late_arrival_count === 0 ? (
        <span className="table__muted">0</span>
      ) : (
        <span className="badge badge--blocking">{formatInteger(row.late_arrival_count)}</span>
      ),
  },
];

/**
 * The retroactive total, said in words under the table.
 *
 * The badges in the column are for scanning; this is for the analyst who scanned
 * past them. A month the insurer already has cannot be corrected through this
 * product — that is the whole point of the sentence, and it is not something a
 * number in a cell can say.
 */
function RetroactiveFootnote({ items }: { items: readonly ReviewCycleSummaryResponse[] }) {
  const affected = items.filter((row) => row.late_arrival_count > 0);
  const total = affected.reduce((sum, row) => sum + row.late_arrival_count, 0);

  if (total === 0) {
    return <p className="panel__footnote">{OVERVIEW_COPY.noRetroactive}</p>;
  }

  return (
    <p className="panel__footnote panel__footnote--warning">
      {OVERVIEW_COPY.retroactiveFootnote(formatInteger(total), formatInteger(affected.length))}
    </p>
  );
}
